import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { SendIcon, BotIcon, UserIcon, SparklesIcon } from 'lucide-react';
const faqs = [{
  id: 1,
  question: 'Do I need any prior design experience?',
  answer: 'No prior experience is required! Our curriculum is designed to take you from complete beginner to job-ready designer. We start with the fundamentals and progressively build your skills. Many of our most successful graduates started with zero design experience.'
}, {
  id: 2,
  question: 'How much time do I need to commit each week?',
  answer: "We recommend dedicating 1-2 hours daily for the best results. This includes watching lessons, completing projects, and attending live sessions (for cohort plans). The self-paced option is fully flexible — learn at your own speed, on your own schedule, with lifetime access to all materials. And since the course content keeps growing, there's always something new to learn."
}, {
  id: 3,
  question: 'What software/tools do I need?',
  answer: "You'll need a computer (Mac or Windows) and access to Figma (free). We'll guide you through setting up all other tools during the program, including Framer, AI tools, and design resources. No expensive software purchases required."
}, {
  id: 4,
  question: "What if I'm not satisfied with the program?",
  answer: "We offer a 14-day money-back guarantee, no questions asked. If you're not completely satisfied within the first 14 days, simply email us and we'll refund your full payment. We're confident you'll love the program."
}, {
  id: 5,
  question: 'How does the job placement guarantee work?',
  answer: "Our Pro Cohort plan includes a job placement guarantee. If you complete all coursework, build your portfolio, and actively apply to jobs for 6 months without landing a design role, we'll refund your tuition in full. Terms and conditions apply."
}, {
  id: 6,
  question: 'Can I pay in installments?',
  answer: 'Installment payments are available for the Pro Cohort plan only — you can split the cost into 2 instalments at no extra charge. The Self-Paced plan requires a one-time full payment. We accept all major credit cards, PayPal, and bank transfers.'
}, {
  id: 7,
  question: 'When does the next cohort start?',
  answer: 'Our next cohort starts in April 2025. Cohorts run every few months throughout the year. If you purchase the self-paced plan, you can start immediately with instant access to all materials.'
}, {
  id: 8,
  question: "I've tried learning to design before and failed. Why is this different?",
  answer: "Most design courses dump content on you and leave you to figure it out alone — that's why people quit. LeaDe is built around accountability and real support. You get a structured week-by-week path, a mentor who knows your name, weekly live Q&A sessions, and a community of peers going through the same journey. You won't get lost, and you won't be alone."
}, {
  id: 9,
  question: 'What if I get stuck?',
  answer: "Getting stuck is part of learning — we've built the program around it. Every week there's a live Q&A session where you can bring your questions and get answers in real time. You also have access to your mentor for 1-on-1 sessions, a private community where fellow students and alumni are always active, and our support team. You'll never be stuck for long."
}, {
  id: 10,
  question: 'How long do I have access?',
  answer: "You get lifetime access to all course materials — videos, resources, templates, and future updates. Once you enroll, the content is yours forever. You can revisit any module at any time, whether you're refreshing your skills a year later or referencing something for a client project."
}, {
  id: 11,
  question: 'How is this different from free YouTube tutorials?',
  answer: "YouTube tutorials are great for one-off tips, but they can't give you structure, support, or community. LeaDe gives you a deliberate learning path where each module builds on the last — no jumping between random videos. You also get mentor feedback on your actual work, weekly Q&A sessions, and a cohort of peers to learn alongside. The difference isn't just the content — it's the environment that makes learning stick."
}, {
  id: 12,
  question: 'Will this work for my side project or startup idea?',
  answer: "Absolutely. Many of our students join specifically to design their own product or validate a startup idea. The curriculum covers everything from user research and wireframing to building in Figma and shipping with Framer — exactly the skills you need to go from idea to a real, polished product. By the end, you'll have the design skills to build and iterate on your own."
}];
type Message = {
  id: string;
  type: 'user' | 'assistant';
  text: string;
};
export function FAQSection() {
  const ref = useRef(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [messages, setMessages] = useState<Message[]>([{
    id: 'welcome',
    type: 'assistant',
    text: "Hi there! I'm the LeaDe assistant. Click on any question below to get an instant answer about our design program."
  }]);
  const [isTyping, setIsTyping] = useState(false);
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, isTyping]);
  const handleQuestionClick = (faq: (typeof faqs)[0]) => {
    if (isTyping) return;
    // Add user message
    const userMsgId = Date.now().toString();
    setMessages(prev => [...prev, {
      id: userMsgId,
      type: 'user',
      text: faq.question
    }]);
    setIsTyping(true);
    // Simulate typing delay
    setTimeout(() => {
      setIsTyping(false);
      const assistantMsgId = (Date.now() + 1).toString();
      setMessages(prev => [...prev, {
        id: assistantMsgId,
        type: 'assistant',
        text: faq.answer
      }]);
    }, 1200);
  };
  return <section id="faq" className="py-24 bg-dark-800 relative overflow-hidden" data-id="element-75">
      {/* Background accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-yellow/5 rounded-full blur-[100px] pointer-events-none" data-id="element-76" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative" ref={ref} data-id="element-77">
        {/* Section Header */}
        <div className="text-center mb-12" data-id="element-78">
          <motion.span initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5
        }} className="inline-block text-brand-yellow text-sm font-semibold tracking-wider uppercase mb-4" data-id="element-79">
            FAQ
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-4xl sm:text-5xl font-bold text-white mb-6" data-id="element-80">
            Ask us anything
            <br data-id="element-81" />
            <span className="text-gradient-yellow" data-id="element-82">Instant answers</span>
          </motion.h2>
        </div>

        {/* Chat Interface */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="bg-dark-900/80 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl shadow-black/50 flex flex-col h-[600px]" data-id="element-83">
          {/* Chat Header */}
          <div className="px-6 py-4 border-b border-white/5 bg-white/5 flex items-center justify-between" data-id="element-84">
            <div className="flex items-center gap-3" data-id="element-85">
              <div className="relative" data-id="element-86">
                <div className="w-10 h-10 rounded-full bg-brand-yellow flex items-center justify-center" data-id="element-87">
                  <BotIcon className="w-6 h-6 text-dark-900" data-id="element-88" />
                </div>
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-dark-900" data-id="element-89"></div>
              </div>
              <div data-id="element-90">
                <h3 className="font-semibold text-white" data-id="element-91">LeaDe Assistant</h3>
                <p className="text-xs text-green-400 flex items-center gap-1" data-id="element-92">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" data-id="element-93"></span>
                  Online
                </p>
              </div>
            </div>
            <div className="text-xs text-gray-500 bg-white/5 px-3 py-1 rounded-full border border-white/5" data-id="element-94">
              AI-powered Support
            </div>
          </div>

          {/* Chat Messages Area */}
          <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-6 space-y-6 scroll-smooth" style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#333 transparent'
        }} data-id="element-95">
            <AnimatePresence initial={false} data-id="element-96">
              {messages.map(msg => <motion.div key={msg.id} initial={{
              opacity: 0,
              y: 20,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} transition={{
              duration: 0.3
            }} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-97">
                  <div className={`flex items-end gap-3 max-w-[85%] ${msg.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-98">
                    {/* Avatar */}
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center overflow-hidden" data-id="element-99">
                      {msg.type === 'user' ? <div className="w-full h-full bg-dark-700 border border-white/10 flex items-center justify-center" data-id="element-100">
                          <UserIcon className="w-4 h-4 text-gray-400" data-id="element-101" />
                        </div> : <div className="w-full h-full bg-brand-yellow flex items-center justify-center" data-id="element-102">
                          <BotIcon className="w-4 h-4 text-dark-900" data-id="element-103" />
                        </div>}
                    </div>

                    {/* Bubble */}
                    <div className={`px-5 py-3.5 text-sm sm:text-base leading-relaxed shadow-lg ${msg.type === 'user' ? 'bg-brand-yellow text-dark-900 rounded-2xl rounded-br-sm font-medium' : 'bg-dark-700/80 text-gray-200 border border-white/10 rounded-2xl rounded-bl-sm backdrop-blur-sm'}`} data-id="element-104">
                      {msg.text}
                    </div>
                  </div>
                </motion.div>)}
            </AnimatePresence>

            {/* Typing Indicator */}
            {isTyping && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} className="flex justify-start" data-id="element-105">
                <div className="flex items-end gap-3" data-id="element-106">
                  <div className="w-8 h-8 rounded-full bg-brand-yellow flex items-center justify-center" data-id="element-107">
                    <BotIcon className="w-4 h-4 text-dark-900" data-id="element-108" />
                  </div>
                  <div className="bg-dark-700/80 border border-white/10 px-4 py-3 rounded-2xl rounded-bl-sm flex items-center gap-1.5" data-id="element-109">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-110"></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-111"></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-112"></span>
                  </div>
                </div>
              </motion.div>}
          </div>

          {/* Question Suggestions (Input Area) */}
          <div className="p-4 bg-dark-800/50 border-t border-white/10 backdrop-blur-md" data-id="element-113">
            <p className="text-xs font-medium text-gray-500 mb-3 uppercase tracking-wider px-1" data-id="element-114">
              Suggested Questions
            </p>
            <div className="flex flex-wrap gap-2 max-h-[140px] overflow-y-auto pr-2 custom-scrollbar" data-id="element-115">
              {faqs.map(faq => <button key={faq.id} onClick={() => handleQuestionClick(faq)} disabled={isTyping} className="text-left px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/10 hover:border-brand-yellow/30 hover:text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95" data-id="element-116">
                  {faq.question}
                </button>)}
            </div>
          </div>
        </motion.div>

        {/* Contact CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="mt-12 text-center" data-id="element-117">
          <p className="text-gray-400 mb-4" data-id="element-118">Still have questions?</p>
          <a href="mailto:learndesign@leade.co" className="inline-flex items-center gap-2 text-brand-yellow hover:text-brand-yellow-dark transition-colors font-medium group" data-id="element-119">
            Contact us
          </a>
        </motion.div>
      </div>
    </section>;
}